import { z } from 'zod'
import type { SimulationIngredient, SimulationPublic } from './simulation'

/**
 * 배합 버전 이력 (C42). 덮어쓰기 저장·되돌리기가 성공하면 저장본 전체를 한 버전으로 남긴다.
 * 서버(apps/api/src/lib/simulation-versions.ts)·앱(versions 화면)·DB(simulation_versions)가 이 값을 같이 쓴다.
 */

/** baseline = 이력을 켜기 전(또는 이력 밖에서 바뀐) 저장본, save = 저장, restore = 예전 버전으로 되돌림 */
export const FORMULA_VERSION_KINDS = ['baseline', 'save', 'restore'] as const
export type FormulaVersionKind = (typeof FORMULA_VERSION_KINDS)[number]

/**
 * 저장 이유 코드. 앱은 자유 글이 아니라 이 코드만 보낸다 (개인정보·4개 언어 표시 때문에).
 * 모르는 코드(더 새 앱)와 옛 앱(칸 없음)은 빈 문자열로 저장한다 — 저장 자체는 막지 않는다
 */
export const FORMULA_VERSION_REASONS = ['manual_edit', 'ai_edit'] as const
export type FormulaVersionReason = (typeof FORMULA_VERSION_REASONS)[number]

export function formulaVersionReason(value: string | undefined): FormulaVersionReason | '' {
  return FORMULA_VERSION_REASONS.find((reason) => reason === value) ?? ''
}

/**
 * 제품 하나에 남기는 버전 수 상한. 넘으면 가장 오래된 것부터 지운다 (지금 버전은 늘 가장 새 것이라 남는다).
 * 대표 결정 전 임시값 — 결과 전체를 한 행씩 쌓으므로 저장 비용과 보관 개인정보를 줄이는 쪽으로 정했다
 */
export const FORMULA_VERSION_KEEP = 30

/**
 * POST /simulations/:id/apply(-local) 에 더하는 선택 칸 (applyEditSchema 가 펼쳐 쓴다). 옛 앱은 보내지 않고,
 * 옛 서버는 모르는 칸을 버린다. 형을 느슨하게 받는 것은 더 새 앱의 값 때문에 저장이 400 으로 막히지 않게 하려는 것
 */
export const formulaVersionApplyFields = {
  /** 앱이 이 저장본과 함께 본 지금 버전 id (없었으면 null). 다르면 같은 내용으로 돌아온 저장(ABA)도 충돌로 본다 */
  baseVersionId: z.string().max(64).nullable().optional(),
  /** 저장 이유 코드 (FORMULA_VERSION_REASONS) */
  versionReason: z.string().max(40).optional(),
  /** 되돌리기: 이 버전의 저장본을 서버가 꺼내 저장한다 (앱이 보낸 result 는 쓰지 않는다) */
  restoreVersionId: z.string().min(1).max(64).optional(),
}

/** GET /simulations/:id/versions 의 한 줄 (저장본은 빼고) */
export interface FormulaVersionSummary {
  id: string
  number: number
  kind: FormulaVersionKind
  reason: FormulaVersionReason | ''
  restoredFromId: string | null
  createdAt: string
  resultRevision: string
}

export interface FormulaVersion extends FormulaVersionSummary {
  result: SimulationPublic
}

type IngredientFacts = Pick<SimulationIngredient, 'name' | 'qty' | 'unit' | 'unitPrice'>

export interface FormulaIngredientChange {
  id: string
  change: 'added' | 'removed' | 'changed'
  before: IngredientFacts | null
  after: IngredientFacts | null
}
export interface FormulaNumberChange {
  before: number
  after: number
}
/** 두 버전의 차이. 숫자 칸은 같으면 null */
export interface FormulaVersionDiff {
  ingredients: FormulaIngredientChange[]
  costPerUnit: FormulaNumberChange | null
  suggestedRetail: FormulaNumberChange | null
  marginPct: FormulaNumberChange | null
  batchUnits: FormulaNumberChange | null
  stepsChanged: boolean
  /** 위에서 다루지 않은 칸(이름·소개·재료 역할·규정 메모 등)이 바뀌었는지 */
  otherChanged: boolean
}

const facts = (i: SimulationIngredient): IngredientFacts => ({
  name: i.name,
  qty: i.qty,
  unit: i.unit,
  unitPrice: i.unitPrice,
})
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
const numberChange = (before: number, after: number) =>
  before === after ? null : { before, after }

/**
 * 배합 비교 (AI·가격 조회 없음). 재료는 id 로 짝짓고 이름·분량·단위·단가만 '바뀜'으로 센다.
 * 원가·판매가·마진·배치 수는 숫자로, 공정은 바뀌었는지만, 나머지는 한 줄 '그 밖의 내용'으로 묶는다
 */
export function compareFormulaVersions(
  before: SimulationPublic,
  after: SimulationPublic,
): FormulaVersionDiff {
  const afterById = new Map(after.ingredients.map((i) => [i.id, i]))
  const beforeIds = new Set(before.ingredients.map((i) => i.id))
  const ingredients: FormulaIngredientChange[] = []
  let ingredientDetailChanged = false
  for (const old of before.ingredients) {
    const next = afterById.get(old.id)
    if (!next) {
      ingredients.push({ id: old.id, change: 'removed', before: facts(old), after: null })
    } else if (!same(facts(old), facts(next))) {
      ingredients.push({ id: old.id, change: 'changed', before: facts(old), after: facts(next) })
    } else if (!same(old, next)) {
      ingredientDetailChanged = true
    }
  }
  for (const next of after.ingredients) {
    if (!beforeIds.has(next.id))
      ingredients.push({ id: next.id, change: 'added', before: null, after: facts(next) })
  }
  const rest = (r: SimulationPublic) => {
    const {
      ingredients: _ingredients,
      steps: _steps,
      plainSteps: _plainSteps,
      costs: _costs,
      pricing: _pricing,
      batch,
      ...others
    } = r
    return { ...others, batch: { ...batch, units: 0 } }
  }
  return {
    ingredients,
    costPerUnit: numberChange(before.costs.totalPerUnit, after.costs.totalPerUnit),
    suggestedRetail: numberChange(before.pricing.suggestedRetail, after.pricing.suggestedRetail),
    marginPct: numberChange(before.pricing.marginPct, after.pricing.marginPct),
    batchUnits: numberChange(before.batch.units, after.batch.units),
    stepsChanged: !same(before.steps, after.steps) || !same(before.plainSteps, after.plainSteps),
    otherChanged: ingredientDetailChanged || !same(rest(before), rest(after)),
  }
}

/** 차이 줄에 쓰는 낱말. 앱은 언어별 문구(FORMULA_VERSIONS_COPY), 관리자는 한국어를 넘긴다 */
export interface FormulaVersionDiffLabels {
  change: Record<FormulaIngredientChange['change'], string>
  costPerUnit: string
  suggestedRetail: string
  marginPct: string
  batchUnits: string
  stepsChanged: string
  otherChanged: string
}

/**
 * 차이를 화면에 한 줄씩 그릴 글로 (버전 기록 화면·견적 상세의 '이전 기준'이 같이 쓴다). 바뀐 것이 없으면 빈 배열.
 * 금액은 화면이 고른 형식(money)으로, 마진은 % 로 쓴다
 */
export function formulaVersionDiffLines(
  diff: FormulaVersionDiff,
  labels: FormulaVersionDiffLabels,
  money: (amount: number) => string,
): string[] {
  const amount = (f: IngredientFacts) => `${f.qty} ${f.unit} · ${money(f.unitPrice)}`
  const ingredients = diff.ingredients.map((change) => {
    const facts = (change.after ?? change.before)!
    const detail =
      change.change === 'changed'
        ? `${amount(change.before!)} → ${amount(change.after!)}`
        : amount(facts)
    return `${labels.change[change.change]} · ${facts.name} ${detail}`
  })
  const percent = (n: number) => `${Math.round(n * 100)}%`
  const numbers = (
    [
      [labels.costPerUnit, diff.costPerUnit, money],
      [labels.suggestedRetail, diff.suggestedRetail, money],
      [labels.marginPct, diff.marginPct, percent],
      [labels.batchUnits, diff.batchUnits, String],
    ] as const
  ).flatMap(([label, change, show]) =>
    change ? [`${label} ${show(change.before)} → ${show(change.after)}`] : [],
  )
  return [
    ...ingredients,
    ...numbers,
    ...(diff.stepsChanged ? [labels.stepsChanged] : []),
    ...(diff.otherChanged ? [labels.otherChanged] : []),
  ]
}
